import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import {
  CREATE_COMMENT,
  CREATE_EDIT,
  CREATE_VERSION,
  DELETE_VERSION,
} from "../../services/graphql/queriesMutations";
import {
  buildWorkspaceGistUrl,
  buildWorkspaceSubjectUrl,
  type WorkspaceGistMode,
} from "../../utils/publicUrls";
import { useGlobalContext } from "../../components/context/AuthContext";
import { GistCommentWorkspacePanel } from "../canonical-comment-workspace";
import type { CanonicalCommentWorkspaceSurface } from "../canonical-comment-workspace";
import NodeActionModal from "../../components/subject/NodeActionModal";
import CreateGistModal from "../../components/subject/CreateGistModal";
import { AdvancedMode } from "./components/AdvancedMode";
import { DiscussionPreviewPanel } from "./components/DiscussionPreviewPanel";
import { GistExplorerPage } from "./components/GistExplorerPage";
import { GistModeSwitch } from "./components/GistModeSwitch";
import { GistReadArticle } from "./components/GistReadArticle";
import { ReadingStripMode } from "./components/ReadingStripMode";
import { SubjectActionMenu } from "./components/SubjectActionMenu";
import { SubjectChooserDrawer } from "./components/SubjectChooserDrawer";
import { SubjectHero } from "./components/SubjectHero";
import { SubjectOverviewPage } from "./components/SubjectOverviewPage";
import { SubjectPickerPanel } from "./components/SubjectPickerPanel";
import { SubjectWorkspaceBreadcrumb } from "./components/SubjectWorkspaceBreadcrumb";
import { ToolsMode } from "./components/ToolsMode";
import { WorkspaceGistNavigator } from "./navigation";
import { useSubjectSingleParams } from "./hooks/useSubjectSingleParams";
import { useSubjectTreeViewModel } from "./hooks/useSubjectTreeViewModel";
import { useSubjectWorkspaceData } from "./hooks/useSubjectWorkspaceData";
import styles from "./SubjectSinglePage.module.css";
import {
  CONTEXT_REPLY_BATCH_SIZE,
  INITIAL_CONTEXT_REPLY_COUNT,
} from "./model/constants";
import type {
  CreateGistTarget,
  GistItem,
  GistReaderMode,
  SubjectActionMenuState,
  SubjectItem,
  VisibleSubjectRow,
} from "./model/types";
import {
  canUserEditGist,
  compactNumber,
  getAllExpandableSubjectIds,
  getFocusedSubjectExpandedIds,
  getInitialFocusedSubjectExpandedIds,
  getNextFocusedSubjectExpandedIds,
  getSubjectDescendantIds,
  getLatestEdit,
  getLatestVersion,
  getFullReaderText,
  getLatestEditBody,
  getSubjectUrl,
  getSummaryText,
  stripHtml,
} from "./model/subjectSingleModel";

const SubjectSinglePage: React.FC = () => {
  const { user } = useGlobalContext();
  const navigate = useNavigate();
  const {
    searchParams,
    setSearchParams,
    routeSubjectPublicId,
    routeGistPublicId,
    readerMode,
    isGistExplorerView,
    selectedGistId,
    initialSubjectId,
  } = useSubjectSingleParams();
  const [selectedSubjectId, setSelectedSubjectId] = useState<number | null>(
    Number.isFinite(initialSubjectId) ? initialSubjectId : null,
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openedGistId, setOpenedGistId] = useState<number | null>(null);
  const [gistExplorerSelectedId, setGistExplorerSelectedId] = useState<
    number | null
  >(null);
  const gistExplorerGridRef = useRef<HTMLDivElement | null>(null);
  const [gistExplorerColumnWeights, setGistExplorerColumnWeights] = useState([
    26, 46, 28,
  ]);
  const gistExplorerColumnWeightsRef = useRef(gistExplorerColumnWeights);

  useEffect(() => {
    gistExplorerColumnWeightsRef.current = gistExplorerColumnWeights;
  }, [gistExplorerColumnWeights]);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [focusedSubjectId, setFocusedSubjectId] = useState<number | null>(null);
  const [hasAutoExpandedTree, setHasAutoExpandedTree] = useState(false);
  const [pickerMode, setPickerMode] = useState<"tree" | "list">("tree");
  const [subjectSearchQuery, setSubjectSearchQuery] = useState("");
  const [searchCollapsedIds, setSearchCollapsedIds] = useState<Set<number>>(
    new Set(),
  );
  const [actionMenu, setActionMenu] = useState<SubjectActionMenuState | null>(
    null,
  );
  const [modalSubject, setModalSubject] = useState<SubjectItem | null>(null);
  const [modalAction, setModalAction] = useState<string>("none");
  const [modalVisible, setModalVisible] = useState(false);
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const [createGistTarget, setCreateGistTarget] =
    useState<CreateGistTarget | null>(null);
  const [summaryEditingGistId, setSummaryEditingGistId] = useState<
    number | null
  >(null);
  const [bodyEditingGistId, setBodyEditingGistId] = useState<number | null>(
    null,
  );
  const [toolsEditingGistId, setToolsEditingGistId] = useState<number | null>(
    null,
  );
  const [summaryDraft, setSummaryDraft] = useState("");
  const [bodyDraft, setBodyDraft] = useState("");
  const [isSavingReaderEdit, setIsSavingReaderEdit] = useState(false);
  const [readerEditError, setReaderEditError] = useState<string | null>(null);
  const [toolsCopyStatus, setToolsCopyStatus] = useState<string | null>(null);
  const [expandedCommentPreviewGistIds, setExpandedCommentPreviewGistIds] =
    useState<Set<number>>(new Set());
  const [activeComposerGistId, setActiveComposerGistId] = useState<
    number | null
  >(null);
  const [inlineCommentDraft, setInlineCommentDraft] = useState("");
  const [submittingCommentGistId, setSubmittingCommentGistId] = useState<
    number | null
  >(null);
  const [commentSubmitErrorByGistId, setCommentSubmitErrorByGistId] = useState<
    Record<number, string>
  >({});
  const [annotationIntentGistId, setAnnotationIntentGistId] = useState<
    number | null
  >(null);
  const [commentWorkspace, setCommentWorkspace] = useState<{
    gistId: number;
    surface: CanonicalCommentWorkspaceSurface;
    selectedCommentId: number | null;
    selectedAnchorId: number | null;
  } | null>(null);
  const [visibleReplyGistCount, setVisibleReplyGistCount] = useState(
    INITIAL_CONTEXT_REPLY_COUNT,
  );
  const rowButtonRefs = useRef<Record<number, HTMLButtonElement | null>>({});
  const actionMenuRef = useRef<HTMLDivElement | null>(null);
  const shouldFocusTreeRowRef = useRef(false);
  const returnFocusSubjectIdRef = useRef<number | null>(null);
  const clickTimeoutRef = useRef<number | null>(null);
  const currentReadingGistRef = useRef<HTMLElement>(null);
  const workspaceNavigatorRef = useRef<HTMLElement | null>(null);
  const workspaceNavigatorWidthRef = useRef(270);
  const [workspaceNavigatorHidden, setWorkspaceNavigatorHidden] = useState(false);
  const [workspaceNavigatorWidth, setWorkspaceNavigatorWidth] = useState(270);

  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current !== null) {
        window.clearTimeout(clickTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    workspaceNavigatorWidthRef.current = workspaceNavigatorWidth;
  }, [workspaceNavigatorWidth]);

  const {
    subjects,
    gists,
    buckets,
    subjectStats,
    rootSubjects,
    selectedSubject,
    selectedPath,
    branchGists,
    directGists,
    activeGists,
    featuredSlots,
    gistTree,
    gistTreeFlat,
    gistExplorerSelectedGist,
    openedGist,
    detailGist,
    detailGistId,
    currentGistSiblingNavigation,
    selectedStats,
    loading,
    error,
    refetchGists,
  } = useSubjectWorkspaceData({
    selectedSubjectId,
    selectedSubjectPublicId: routeSubjectPublicId,
    selectedGistPublicId: routeGistPublicId,
    openedGistId,
    gistExplorerSelectedId,
    selectedGistId,
  });
  const activeSubjectId = selectedSubject?.id ?? selectedSubjectId;
  const activeSelectedGistId = detailGist?.id ?? selectedGistId;
  const linkableGists = useMemo(() => {
    const uniqueById = new Map<number, GistItem>();
    gists.forEach((gist) => uniqueById.set(gist.id, gist));
    return Array.from(uniqueById.values());
  }, [gists]);

  const workspaceNavigationGists = useMemo(() => {
    const uniqueById = new Map<number, GistItem>();
    branchGists.forEach((gist) => uniqueById.set(gist.id, gist));
    if (detailGist) uniqueById.set(detailGist.id, detailGist);
    return Array.from(uniqueById.values());
  }, [branchGists, detailGist]);

  const [createCommentMutation] = useMutation(CREATE_COMMENT);
  const [createVersionMutation] = useMutation(CREATE_VERSION);
  const [createEditMutation] = useMutation(CREATE_EDIT);
  const [deleteVersionMutation] = useMutation(DELETE_VERSION);

  useEffect(() => {
    setVisibleReplyGistCount(INITIAL_CONTEXT_REPLY_COUNT);
    setToolsEditingGistId(null);
    setSummaryDraft("");
    setBodyDraft("");
    setReaderEditError(null);
  }, [detailGistId]);

  useEffect(() => {
    if (detailGistId === null) return;

    const timeoutId = window.setTimeout(() => {
      currentReadingGistRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 80);

    return () => window.clearTimeout(timeoutId);
  }, [detailGistId, readerMode]);

  const preferredPickerSubject = useMemo(() => {
    const news = subjects.find(
      (subject) => subject.title.trim().toLowerCase() === "news",
    );
    if (news) return news;
    return (
      rootSubjects
        .slice()
        .sort(
          (a, b) =>
            (subjectStats[b.id]?.branchGistCount || 0) -
            (subjectStats[a.id]?.branchGistCount || 0),
        )[0] || null
    );
  }, [rootSubjects, subjectStats, subjects]);
  const {
    normalizedSubjectSearchQuery,
    isSubjectSearchActive,
    visibleSubjectRows,
    effectiveSubjectExpandedIds,
    subjectSearchMatchCount,
    subjectSearchMatchingIds,
    firstSubjectSearchMatchId,
    focusedSubject,
  } = useSubjectTreeViewModel({
    subjectSearchQuery,
    rootSubjects,
    buckets,
    expandedIds,
    pickerMode,
    searchCollapsedIds,
    focusedSubjectId,
    preferredPickerSubject,
  });

  useEffect(() => {
    setSearchCollapsedIds(new Set());
  }, [normalizedSubjectSearchQuery]);

  useEffect(() => {
    const value = searchParams.get("subjectId");
    const numericValue = value ? Number(value) : NaN;
    const nextId = Number.isFinite(numericValue) ? numericValue : null;
    setSelectedSubjectId(nextId);
  }, [searchParams]);

  useEffect(() => {
    if (selectedSubjectId === null) return;
    if (!subjects.length) return;
    if (!subjects.some((subject) => subject.id === selectedSubjectId)) {
      setSearchParams({}, { replace: true });
    }
  }, [selectedSubjectId, setSearchParams, subjects]);

  useEffect(() => {
    if (activeSubjectId === null || !subjects.length) return;
    if (isSubjectSearchActive) return;

    setExpandedIds(
      getFocusedSubjectExpandedIds(
        activeSubjectId,
        subjects,
        rootSubjects,
        buckets,
      ),
    );
    setFocusedSubjectId(activeSubjectId);
    setHasAutoExpandedTree(true);
  }, [activeSubjectId, buckets, isSubjectSearchActive, rootSubjects, subjects]);

  useEffect(() => {
    setOpenedGistId(featuredSlots[0]?.gist?.id || activeGists[0]?.id || null);
  }, [activeGists, activeSubjectId, featuredSlots]);

  useEffect(() => {
    if (Number.isFinite(activeSelectedGistId)) {
      setGistExplorerSelectedId(activeSelectedGistId);
    } else {
      const currentSelectionIsValid = gistTreeFlat.some(
        (gist) => gist.id === gistExplorerSelectedId,
      );
      if (!currentSelectionIsValid) {
        setGistExplorerSelectedId(gistTreeFlat[0]?.id || null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSelectedGistId, activeSubjectId, gistTreeFlat]);

  useEffect(() => {
    if (!subjects.length || hasAutoExpandedTree) return;
    if (activeSubjectId !== null) return;
    setExpandedIds(getInitialFocusedSubjectExpandedIds(rootSubjects, buckets));
    setHasAutoExpandedTree(true);
  }, [
    buckets,
    hasAutoExpandedTree,
    rootSubjects,
    activeSubjectId,
    subjects.length,
  ]);

  useEffect(() => {
    if (!visibleSubjectRows.length) {
      if (focusedSubjectId !== null) setFocusedSubjectId(null);
      return;
    }

    if (
      focusedSubjectId !== null &&
      visibleSubjectRows.some((row) => row.subject.id === focusedSubjectId)
    ) {
      return;
    }

    const preferredVisible = preferredPickerSubject
      ? visibleSubjectRows.find(
          (row) => row.subject.id === preferredPickerSubject.id,
        )
      : null;
    setFocusedSubjectId((preferredVisible || visibleSubjectRows[0]).subject.id);
  }, [focusedSubjectId, preferredPickerSubject, visibleSubjectRows]);

  useEffect(() => {
    if (!shouldFocusTreeRowRef.current || focusedSubjectId === null) return;
    rowButtonRefs.current[focusedSubjectId]?.focus();
    shouldFocusTreeRowRef.current = false;
  }, [focusedSubjectId, visibleSubjectRows]);

  useEffect(() => {
    if (!actionMenu) return;
    window.setTimeout(() => {
      const firstItem = actionMenuRef.current?.querySelector<HTMLButtonElement>(
        'button[role="menuitem"]:not(:disabled)',
      );
      firstItem?.focus();
    }, 0);
  }, [actionMenu]);

  useEffect(() => {
    setSummaryEditingGistId(null);
    setBodyEditingGistId(null);
    setSummaryDraft("");
    setBodyDraft("");
    setReaderEditError(null);
    setToolsCopyStatus(null);
  }, [detailGist?.id]);

  useEffect(() => {
    if (!toolsCopyStatus) return;
    const timeoutId = window.setTimeout(() => setToolsCopyStatus(null), 1800);
    return () => window.clearTimeout(timeoutId);
  }, [toolsCopyStatus]);


  const focusSubjectBranch = (subjectId: number) => {
    if (isSubjectSearchActive) return;
    setExpandedIds(
      getFocusedSubjectExpandedIds(subjectId, subjects, rootSubjects, buckets),
    );
    setFocusedSubjectId(subjectId);
  };

  const workspaceModeForReaderMode = (mode: GistReaderMode): WorkspaceGistMode => {
    if (mode === "tools") return "edit";
    return mode;
  };

  const navigateToWorkspaceSubject = (subject: SubjectItem, view?: "gists") => {
    focusSubjectBranch(subject.id);
    if (subject.publicId) {
      const params = new URLSearchParams();
      if (view) params.set("view", view);
      const suffix = params.toString() ? `?${params.toString()}` : "";
      navigate(`${buildWorkspaceSubjectUrl(subject)}${suffix}`);
    } else {
      const params: Record<string, string> = { subjectId: String(subject.id) };
      if (view) params.view = view;
      setSearchParams(params);
    }
    setDrawerOpen(false);
    setActionMenu(null);
  };

  const navigateToWorkspaceGist = (
    gist: GistItem,
    mode: GistReaderMode = "read",
    options: { preserveAnnotationIntent?: boolean } = {},
  ) => {
    if (!options.preserveAnnotationIntent) setAnnotationIntentGistId(null);
    if (gist.publicId) {
      navigate(buildWorkspaceGistUrl(gist, workspaceModeForReaderMode(mode)));
    } else if (selectedSubject) {
      setSearchParams({
        subjectId: String(selectedSubject.id),
        gistId: String(gist.id),
        mode,
      });
    }
    setDrawerOpen(false);
    setActionMenu(null);
  };

  const chooseSubject = (subjectId: number) => {
    const subject = subjects.find((candidate) => candidate.id === subjectId);
    if (!subject) return;
    navigateToWorkspaceSubject(subject);
  };

  const viewSubjectGists = (subjectId: number) => {
    const subject = subjects.find((candidate) => candidate.id === subjectId);
    if (!subject) return;
    navigateToWorkspaceSubject(subject, "gists");
  };

  const openGistReader = (gist: GistItem, mode: GistReaderMode = "read") => {
    if (!selectedSubject) return;
    navigateToWorkspaceGist(gist, mode);
  };

  const openGistAnnotationWorkspace = (gist: GistItem) => {
    if (!selectedSubject) return;
    setAnnotationIntentGistId(gist.id);
    navigateToWorkspaceGist(gist, "advanced", { preserveAnnotationIntent: true });
  };

  const openCommentWorkspace = (gist: GistItem) => {
    setCommentWorkspace({
      gistId: gist.id,
      surface: "docked",
      selectedCommentId: null,
      selectedAnchorId: null,
    });
    setDrawerOpen(false);
    setActionMenu(null);
  };

  const openCommentWorkspaceForAnchor = (gist: GistItem, anchorId: number) => {
    setCommentWorkspace((current) => ({
      gistId: gist.id,
      surface: current?.gistId === gist.id ? current.surface : "docked",
      selectedCommentId: null,
      selectedAnchorId: anchorId,
    }));
    setDrawerOpen(false);
    setActionMenu(null);
  };

  const closeCommentWorkspace = () => {
    setCommentWorkspace(null);
  };

  const setCommentWorkspaceSurface = (surface: CanonicalCommentWorkspaceSurface) => {
    setCommentWorkspace((current) => (current ? { ...current, surface } : current));
  };

  const toggleCommentPreview = (gistId: number) => {
    setExpandedCommentPreviewGistIds((current) => {
      const next = new Set(current);
      if (next.has(gistId)) {
        next.delete(gistId);
      } else {
        next.add(gistId);
      }
      return next;
    });
  };

  const openInlineCommentComposer = (gistId: number) => {
    setExpandedCommentPreviewGistIds((current) => new Set(current).add(gistId));
    setActiveComposerGistId(gistId);
    setInlineCommentDraft("");
    setCommentSubmitErrorByGistId((current) => {
      const next = { ...current };
      delete next[gistId];
      return next;
    });
  };

  const cancelInlineCommentComposer = () => {
    setActiveComposerGistId(null);
    setInlineCommentDraft("");
  };

  const submitInlineComment = async (gist: GistItem) => {
    const comment = inlineCommentDraft.trim();
    if (!comment) return;

    if (!user) {
      setCommentSubmitErrorByGistId((current) => ({
        ...current,
        [gist.id]: "Please log in to add a comment.",
      }));
      return;
    }

    const latestEdit = getLatestEdit(gist);
    if (!latestEdit?.id) {
      setCommentSubmitErrorByGistId((current) => ({
        ...current,
        [gist.id]: "This gist is missing an editable comment target.",
      }));
      return;
    }

    setSubmittingCommentGistId(gist.id);
    setCommentSubmitErrorByGistId((current) => {
      const next = { ...current };
      delete next[gist.id];
      return next;
    });

    try {
      await createCommentMutation({
        variables: {
          newComment: {
            comment,
            parentId: null,
            gistId: gist.id,
            editId: latestEdit.id,
          },
        },
      });
      await refetchGists();
      setInlineCommentDraft("");
      setActiveComposerGistId(null);
      setExpandedCommentPreviewGistIds((current) => new Set(current).add(gist.id));
    } catch (error: any) {
      setCommentSubmitErrorByGistId((current) => ({
        ...current,
        [gist.id]: error?.message || "Failed to add comment. Please try again.",
      }));
    } finally {
      setSubmittingCommentGistId(null);
    }
  };

  const changeReaderMode = (mode: GistReaderMode) => {
    if (!selectedSubject || !detailGist) return;
    navigateToWorkspaceGist(detailGist, mode);
  };

  const getWorkspaceGistUrl = (gist: GistItem, mode: GistReaderMode = "read") => {
    if (gist.publicId) {
      return buildWorkspaceGistUrl(gist, workspaceModeForReaderMode(mode));
    }
    const subjectId = selectedSubject?.id || gist.subjectId || "";
    const params = new URLSearchParams({
      subjectId: String(subjectId),
      gistId: String(gist.id),
      mode,
    });
    return `/subject-single?${params.toString()}`;
  };

  const getNavigatorGistUrl = (gist: GistItem) => {
    const params = new URLSearchParams();
    if (selectedSubject?.id || gist.subjectId) {
      params.set("subjectId", String(selectedSubject?.id || gist.subjectId));
    }
    params.set("gistId", String(gist.id));
    return `/navigator?${params.toString()}`;
  };

  const copyWorkspaceGistLink = async (gist: GistItem) => {
    const link = `${window.location.origin}${getWorkspaceGistUrl(gist, "read")}`;
    try {
      await window.navigator.clipboard.writeText(link);
      setToolsCopyStatus("Workspace link copied");
    } catch {
      setToolsCopyStatus("Copy failed");
    }
  };

  const returnToSubjectOverview = () => {
    if (!selectedSubject) return;
    navigateToWorkspaceSubject(selectedSubject);
  };

  const focusSubject = (subjectId: number) => {
    shouldFocusTreeRowRef.current = true;
    setFocusedSubjectId(subjectId);
  };

  const clampActionMenuPosition = (x: number, y: number) => {
    const menuWidth = 240;
    const menuHeight = 280;
    const margin = 14;
    let nextX = x || window.innerWidth / 2;
    let nextY = y || window.innerHeight / 2;

    if (nextX + menuWidth + margin > window.innerWidth) {
      nextX = window.innerWidth - menuWidth - margin;
    }
    if (nextY + menuHeight + margin > window.innerHeight) {
      nextY = window.innerHeight - menuHeight - margin;
    }

    return { x: Math.max(margin, nextX), y: Math.max(margin, nextY) };
  };

  const showSubjectActionMenu = (
    subject: SubjectItem,
    x: number,
    y: number,
  ) => {
    const position = clampActionMenuPosition(x, y);
    returnFocusSubjectIdRef.current = subject.id;
    setFocusedSubjectId(subject.id);
    setActionMenu({ subject, x: position.x, y: position.y });
  };

  const closeActionMenu = (restoreFocus = true) => {
    const returnId = returnFocusSubjectIdRef.current;
    setActionMenu(null);

    if (restoreFocus && returnId !== null) {
      shouldFocusTreeRowRef.current = true;
      setFocusedSubjectId(returnId);
      window.requestAnimationFrame(() =>
        rowButtonRefs.current[returnId]?.focus(),
      );
    }
  };


  const handleSubjectClick = (
    event: React.MouseEvent,
    subject: SubjectItem,
    isExplorer: boolean,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if (clickTimeoutRef.current !== null) {
      window.clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }

    const hasChildren = (buckets[String(subject.id)] || []).length > 0;
    const shouldExpandInPicker =
      !isExplorer &&
      pickerMode === "tree" &&
      hasChildren &&
      !effectiveSubjectExpandedIds.has(subject.id);

    if (shouldExpandInPicker) {
      setFocusedSubjectId(subject.id);
      setExpandedIds((current) =>
        getNextFocusedSubjectExpandedIds(
          subject.id,
          subjects,
          rootSubjects,
          buckets,
          current,
        ),
      );
      return;
    }

    clickTimeoutRef.current = window.setTimeout(() => {
      clickTimeoutRef.current = null;
      viewSubjectGists(subject.id);
    }, 200);
  };

  const handleSubjectDoubleClick = (
    event: React.MouseEvent,
    subject: SubjectItem,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if (clickTimeoutRef.current !== null) {
      window.clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }

    showSubjectActionMenu(subject, event.clientX, event.clientY);
  };


  const openSubjectModal = (
    subject: SubjectItem,
    action: "add" | "update" | "show" | "delete",
  ) => {
    closeActionMenu(false);
    setModalSubject(subject);
    setModalAction(action);
    setModalPosition({ x: window.innerWidth / 2, y: window.innerHeight / 3 });
    setModalVisible(true);
  };

  const closeSubjectModal = () => {
    setModalVisible(false);
    setModalSubject(null);
    setModalAction("none");
  };

  const openCreateGistModal = (target: CreateGistTarget) => {
    closeActionMenu(false);
    setCreateGistTarget(target);
  };

  const openLinkedDraftModal = (gist: GistItem) => {
    openCreateGistModal({
      subjectId: gist.subjectId || selectedSubject?.id || 0,
      parentGistId: gist.id,
      defaultSaveAsDraft: true,
      intent: "linked-draft",
    });
  };

  const closeCreateGistModal = () => {
    setCreateGistTarget(null);
  };

  const isRegisteredUser = Boolean(user);
  const isSubjectModerator =
    user?.userType === "admin" || user?.userType === "moderator";

  const toggleExpanded = (subjectId: number) => {
    if (isSubjectSearchActive) {
      setSearchCollapsedIds((current) => {
        const next = new Set(current);
        if (effectiveSubjectExpandedIds.has(subjectId)) {
          next.add(subjectId);
        } else {
          next.delete(subjectId);
        }
        return next;
      });
      return;
    }

    setExpandedIds((current) =>
      getNextFocusedSubjectExpandedIds(
        subjectId,
        subjects,
        rootSubjects,
        buckets,
        current,
      ),
    );
  };

  const expandAll = () => {
    setExpandedIds(getAllExpandableSubjectIds(subjects, buckets));
  };

  const collapseAll = () => {
    setExpandedIds(getInitialFocusedSubjectExpandedIds(rootSubjects, buckets));
  };

  const collapseSubject = (subjectId: number) => {
    if (isSubjectSearchActive) {
      setSearchCollapsedIds((current) => new Set(current).add(subjectId));
      return;
    }

    setExpandedIds((current) => {
      const next = new Set(current);
      next.delete(subjectId);
      getSubjectDescendantIds(subjectId, buckets).forEach((descendantId) =>
        next.delete(descendantId),
      );
      return next;
    });
  };

  const handleSubjectTreeKeyDown = (event: React.KeyboardEvent) => {
    if (!visibleSubjectRows.length) return;

    const activeId =
      focusedSubjectId ??
      focusedSubject?.id ??
      visibleSubjectRows[0].subject.id;
    const activeIndex = Math.max(
      0,
      visibleSubjectRows.findIndex((row) => row.subject.id === activeId),
    );
    const activeRow = visibleSubjectRows[activeIndex] || visibleSubjectRows[0];
    const activeSubject = activeRow.subject;
    const activeChildren = buckets[String(activeSubject.id)] || [];
    const parentSubject =
      typeof activeSubject.parentId === "number"
        ? subjects.find((subject) => subject.id === activeSubject.parentId) ||
          null
        : null;

    const moveToRow = (row: VisibleSubjectRow | undefined) => {
      if (!row) return;
      event.preventDefault();
      focusSubject(row.subject.id);
    };

    switch (event.key) {
      case "ArrowDown":
        moveToRow(
          visibleSubjectRows[
            Math.min(activeIndex + 1, visibleSubjectRows.length - 1)
          ],
        );
        break;
      case "ArrowUp":
        moveToRow(visibleSubjectRows[Math.max(activeIndex - 1, 0)]);
        break;
      case "Home":
        moveToRow(visibleSubjectRows[0]);
        break;
      case "End":
        moveToRow(visibleSubjectRows[visibleSubjectRows.length - 1]);
        break;
      case "ArrowRight":
        if (
          pickerMode === "tree" &&
          activeChildren.length > 0 &&
          !effectiveSubjectExpandedIds.has(activeSubject.id)
        ) {
          event.preventDefault();
          toggleExpanded(activeSubject.id);
          focusSubject(activeSubject.id);
        } else if (
          pickerMode === "tree" &&
          activeChildren.length > 0 &&
          effectiveSubjectExpandedIds.has(activeSubject.id)
        ) {
          moveToRow(
            visibleSubjectRows.find(
              (row) =>
                row.subject.parentId === activeSubject.id &&
                row.depth === activeRow.depth + 1,
            ),
          );
        }
        break;
      case "ArrowLeft":
        if (
          pickerMode === "tree" &&
          activeChildren.length > 0 &&
          effectiveSubjectExpandedIds.has(activeSubject.id)
        ) {
          event.preventDefault();
          collapseSubject(activeSubject.id);
          focusSubject(activeSubject.id);
        } else if (pickerMode === "tree" && parentSubject) {
          moveToRow(
            visibleSubjectRows.find(
              (row) => row.subject.id === parentSubject.id,
            ),
          );
        }
        break;
      case "Enter":
      case " ":
      case "Spacebar":
        event.preventDefault();
        viewSubjectGists(activeSubject.id);
        break;
      case "Escape":
        if (actionMenu) {
          event.preventDefault();
          closeActionMenu();
        }
        break;
      default:
        break;
    }
  };

  const clearSubjectSearch = () => {
    setSubjectSearchQuery("");
    setSearchCollapsedIds(new Set());
  };

  const openFirstSubjectSearchMatch = () => {
    if (firstSubjectSearchMatchId === null) return;
    viewSubjectGists(firstSubjectSearchMatchId);
  };

  const handleSubjectSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Escape" && subjectSearchQuery) {
      event.preventDefault();
      clearSubjectSearch();
      return;
    }

    if (event.key === "Enter" && firstSubjectSearchMatchId !== null) {
      event.preventDefault();
      openFirstSubjectSearchMatch();
    }
  };

  const renderHighlightedSubjectTitle = (title: string) => {
    if (!normalizedSubjectSearchQuery) return title;

    const matchStart = title
      .toLowerCase()
      .indexOf(normalizedSubjectSearchQuery);
    if (matchStart < 0) return title;

    const matchEnd = matchStart + normalizedSubjectSearchQuery.length;
    return (
      <>
        {title.slice(0, matchStart)}
        <mark className={styles.subjectSearchHighlight}>
          {title.slice(matchStart, matchEnd)}
        </mark>
        {title.slice(matchEnd)}
      </>
    );
  };

  const renderSubjectSearch = (label: string) => (
    <div className={styles.subjectSearchBar}>
      <div className={styles.subjectSearchInputWrap}>
        <input
          type="search"
          value={subjectSearchQuery}
          onChange={(event) => setSubjectSearchQuery(event.target.value)}
          onKeyDown={handleSubjectSearchKeyDown}
          placeholder="Search subjects..."
          aria-label={label}
        />
        {subjectSearchQuery && (
          <button
            type="button"
            className={styles.subjectSearchClear}
            onClick={clearSubjectSearch}
            aria-label="Clear subject search"
          >
            ×
          </button>
        )}
      </div>
      {isSubjectSearchActive && (
        <span className={styles.subjectSearchCount} aria-live="polite">
          {subjectSearchMatchCount === 1
            ? "1 match"
            : `${subjectSearchMatchCount} matches`}
        </span>
      )}
    </div>
  );

  const renderSubjectSearchEmpty = () => (
    <div className={styles.subjectSearchEmpty}>No subjects found</div>
  );

  const handleActionMenuKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
  ) => {
    const menuItems = Array.from(
      actionMenuRef.current?.querySelectorAll(
        'button[role="menuitem"]:not(:disabled)',
      ) || [],
    ) as HTMLButtonElement[];

    if (event.key === "Escape") {
      event.preventDefault();
      closeActionMenu();
      return;
    }

    if (!menuItems.length) return;

    const currentIndex = Math.max(
      0,
      menuItems.indexOf(document.activeElement as HTMLButtonElement),
    );
    if (event.key === "ArrowDown") {
      event.preventDefault();
      menuItems[(currentIndex + 1) % menuItems.length].focus();
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      menuItems[
        (currentIndex - 1 + menuItems.length) % menuItems.length
      ].focus();
    }
    if (event.key === "Home") {
      event.preventDefault();
      menuItems[0].focus();
    }
    if (event.key === "End") {
      event.preventDefault();
      menuItems[menuItems.length - 1].focus();
    }
  };

  const beginSummaryEdit = (gist: GistItem) => {
    if (!canUserEditGist(gist, user)) return;
    setReaderEditError(null);
    setSummaryDraft(getSummaryText(gist));
    setSummaryEditingGistId(gist.id);
  };

  const beginBodyEdit = (gist: GistItem) => {
    if (!canUserEditGist(gist, user)) return;
    setReaderEditError(null);
    setBodyDraft(getLatestEditBody(gist) || getFullReaderText(gist));
    setBodyEditingGistId(gist.id);
  };

  const beginToolsEdit = (gist: GistItem) => {
    if (!canUserEditGist(gist, user)) return;
    setReaderEditError(null);
    setSummaryEditingGistId(null);
    setBodyEditingGistId(null);
    setSummaryDraft(getSummaryText(gist));
    setBodyDraft(getLatestEditBody(gist) || getFullReaderText(gist));
    setToolsEditingGistId(gist.id);
  };

  const cancelReaderEdit = () => {
    setSummaryEditingGistId(null);
    setBodyEditingGistId(null);
    setSummaryDraft("");
    setBodyDraft("");
    setReaderEditError(null);
  };

  const cancelToolsEdit = () => {
    setToolsEditingGistId(null);
    setSummaryDraft("");
    setBodyDraft("");
    setReaderEditError(null);
  };

  const saveGistRevision = async (
    gist: GistItem,
    nextSummaryValue: string,
    nextBodyValue: string,
  ): Promise<boolean> => {
    const activeVersion = getLatestVersion(gist);
    const activeEdit = getLatestEdit(gist);
    if (!user || !activeVersion) return false;

    const originalSummary = stripHtml(activeVersion.point || "").trim();
    const originalBody = activeEdit?.body || "";
    const nextSummary = nextSummaryValue.trim();
    const nextBody = nextBodyValue;
    const summaryChanged = nextSummary !== originalSummary;
    const bodyChanged = nextBody !== originalBody;

    if (!summaryChanged && !bodyChanged) {
      return true;
    }

    setIsSavingReaderEdit(true);
    setReaderEditError(null);

    let createdVersionId: number | undefined;

    try {
      if (summaryChanged) {
        const versionResponse = await createVersionMutation({
          variables: {
            version: {
              gistId: gist.id,
              point: nextSummary || "Untitled point",
            },
          },
        });
        createdVersionId = versionResponse.data?.addVersion?.id;

        if (!createdVersionId) {
          throw new Error("The server did not return a new version id.");
        }

        await createEditMutation({
          variables: {
            edit: {
              versionId: createdVersionId,
              body: nextBody || originalBody || "",
            },
          },
        });
      } else if (bodyChanged) {
        await createEditMutation({
          variables: {
            edit: {
              versionId: activeVersion.id,
              body: nextBody,
            },
          },
        });
      }

      await refetchGists();
      return true;
    } catch (error) {
      if (createdVersionId) {
        try {
          await deleteVersionMutation({ variables: { id: createdVersionId } });
        } catch {
          // Best-effort cleanup only. Keep the original error visible to the user.
        }
      }
      setReaderEditError(
        error instanceof Error
          ? error.message
          : "Unable to save the gist changes.",
      );
      return false;
    } finally {
      setIsSavingReaderEdit(false);
    }
  };

  const saveReaderEdit = async (gist: GistItem) => {
    const activeVersion = getLatestVersion(gist);
    const activeEdit = getLatestEdit(gist);
    if (!activeVersion) return;

    const originalSummary = stripHtml(activeVersion.point || "").trim();
    const originalBody = activeEdit?.body || "";
    const isSummaryEditing = summaryEditingGistId === gist.id;
    const isBodyEditing = bodyEditingGistId === gist.id;
    const nextSummary = isSummaryEditing ? summaryDraft : originalSummary;
    const nextBody = isBodyEditing ? bodyDraft : originalBody;
    const saved = await saveGistRevision(gist, nextSummary, nextBody);
    if (saved) cancelReaderEdit();
  };

  const saveToolsEdit = async (gist: GistItem) => {
    const saved = await saveGistRevision(gist, summaryDraft, bodyDraft);
    if (saved) cancelToolsEdit();
  };

  const footerSubject = focusedSubject || preferredPickerSubject;



  const focusWorkspaceNavigator = () => {
    const navigatorElement = workspaceNavigatorRef.current;
    if (!navigatorElement) return;

    navigatorElement.scrollIntoView({ block: "center", behavior: "smooth" });
    window.requestAnimationFrame(() => {
      navigatorElement.focus({ preventScroll: true });
    });
  };

  const revealWorkspaceNavigator = () => {
    setWorkspaceNavigatorHidden(false);
    window.requestAnimationFrame(() => {
      focusWorkspaceNavigator();
    });
  };

  const startWorkspaceNavigatorResize = (
    event: React.PointerEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const initialWidth = workspaceNavigatorWidthRef.current;
    const minWidth = 220;
    const maxWidth = 460;

    document.body.classList.add(styles.workspaceNavigatorResizingBody);

    const handlePointerMove = (moveEvent: PointerEvent) => {
      const delta = moveEvent.clientX - startX;
      const nextWidth = Math.min(
        maxWidth,
        Math.max(minWidth, initialWidth + delta),
      );
      setWorkspaceNavigatorWidth(nextWidth);
    };

    const handlePointerUp = () => {
      document.body.classList.remove(styles.workspaceNavigatorResizingBody);
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);
  };

  const renderSubjectHero = () => {
    if (!selectedSubject || !selectedStats) return null;
    return (
      <SubjectHero
        subject={selectedSubject}
        stats={selectedStats}
        onBackToSubject={returnToSubjectOverview}
        onBranchGistsClick={detailGist ? revealWorkspaceNavigator : undefined}
      />
    );
  };

  const renderModeSwitch = () => (
    <GistModeSwitch
      subjectTitle={selectedSubject?.title}
      readerMode={readerMode}
      onBackToSubject={returnToSubjectOverview}
      onChangeMode={changeReaderMode}
    />
  );

  const renderReadArticle = (gist: GistItem, withMarkers = false) => {
    const activeWorkspaceAnchorId =
      commentWorkspace?.gistId === gist.id ? commentWorkspace.selectedAnchorId : null;

    return (
      <GistReadArticle
        gist={gist}
        user={user}
        withMarkers={withMarkers}
        summaryDraft={summaryDraft}
        bodyDraft={bodyDraft}
        readerEditError={readerEditError}
        isSavingReaderEdit={isSavingReaderEdit}
        isSummaryEditing={summaryEditingGistId === gist.id}
        isBodyEditing={bodyEditingGistId === gist.id}
        onBeginSummaryEdit={beginSummaryEdit}
        onBeginBodyEdit={beginBodyEdit}
        onCancelReaderEdit={cancelReaderEdit}
        onSaveReaderEdit={saveReaderEdit}
        onSummaryDraftChange={setSummaryDraft}
        onBodyDraftChange={setBodyDraft}
        onOpenCommentWorkspaceForAnchor={openCommentWorkspaceForAnchor}
        activeWorkspaceAnchorId={activeWorkspaceAnchorId}
        canCreateLinkedDraft={isRegisteredUser}
        onCreateLinkedDraft={openLinkedDraftModal}
        linkableGists={linkableGists}
        linkableSubjects={subjects}
        currentSubjectId={selectedSubject?.id ?? gist.subjectId ?? null}
        getInternalGistLinkUrl={(targetGist) => getWorkspaceGistUrl(targetGist, "read")}
      />
    );
  };

  const renderDiscussionPreview = (gist: GistItem) => (
    <DiscussionPreviewPanel
      gist={gist}
      user={user}
      isComposerActive={activeComposerGistId === gist.id}
      isSubmittingComment={submittingCommentGistId === gist.id}
      submitError={commentSubmitErrorByGistId[gist.id]}
      inlineCommentDraft={inlineCommentDraft}
      onOpenComposer={openInlineCommentComposer}
      onSubmitComment={submitInlineComment}
      onCancelComposer={cancelInlineCommentComposer}
      onDraftChange={setInlineCommentDraft}
      onOpenWorkspace={openCommentWorkspace}
    />
  );

  const renderReadMode = (gist: GistItem) => (
    <ReadingStripMode
      gist={gist}
      gists={gists}
      selectedSubjectId={selectedSubject?.id ?? null}
      isRegisteredUser={isRegisteredUser}
      readerMode={readerMode}
      visibleReplyGistCount={visibleReplyGistCount}
      expandedCommentPreviewGistIds={expandedCommentPreviewGistIds}
      currentReadingGistRef={currentReadingGistRef}
      currentGistSiblingNavigation={currentGistSiblingNavigation}
      renderReadArticle={renderReadArticle}
      renderDiscussionPreview={renderDiscussionPreview}
      onOpenGistReader={openGistReader}
      onOpenCommentWorkspace={openCommentWorkspace}
      onOpenGistAnnotationWorkspace={openGistAnnotationWorkspace}
      onOpenCreateGistModal={openCreateGistModal}
      onChangeReaderMode={changeReaderMode}
      onToggleCommentPreview={toggleCommentPreview}
      onLoadMoreReplies={() =>
        setVisibleReplyGistCount(
          (current) => current + CONTEXT_REPLY_BATCH_SIZE,
        )
      }
    />
  );

  const renderToolsMode = (gist: GistItem) => (
    <ToolsMode
      gist={gist}
      user={user}
      gists={gists}
      selectedSubjectId={selectedSubject?.id ?? null}
      isRegisteredUser={isRegisteredUser}
      toolsEditingGistId={toolsEditingGistId}
      summaryDraft={summaryDraft}
      bodyDraft={bodyDraft}
      readerEditError={readerEditError}
      isSavingReaderEdit={isSavingReaderEdit}
      toolsCopyStatus={toolsCopyStatus}
      readModeContent={renderReadMode(gist)}
      onCreateReply={(subjectId, parentGistId) =>
        openCreateGistModal({ subjectId, parentGistId })
      }
      onChangeReaderMode={changeReaderMode}
      onBeginToolsEdit={beginToolsEdit}
      onCopyWorkspaceGistLink={copyWorkspaceGistLink}
      getNavigatorGistUrl={getNavigatorGistUrl}
      onCancelToolsEdit={cancelToolsEdit}
      onSaveToolsEdit={saveToolsEdit}
      onSummaryDraftChange={setSummaryDraft}
      onBodyDraftChange={setBodyDraft}
      getInternalGistLinkUrl={(targetGist) => getWorkspaceGistUrl(targetGist, "read")}
    />
  );

  const renderAdvancedMode = (gist: GistItem) => (
    <AdvancedMode
      gist={gist}
      gists={gists}
      visibleReplyGistCount={visibleReplyGistCount}
      annotationIntentGistId={annotationIntentGistId}
      onOpenGistReader={openGistReader}
      onDismissAnnotationHint={() => setAnnotationIntentGistId(null)}
      onLoadMoreReplies={() =>
        setVisibleReplyGistCount(
          (current) => current + CONTEXT_REPLY_BATCH_SIZE,
        )
      }
      onAfterDelete={async () => {
        if (typeof refetchGists === "function") {
          await refetchGists();
        }
      }}
    />
  );

  const renderWorkspaceNavigator = (gist: GistItem) => (
    <WorkspaceGistNavigator
      gists={workspaceNavigationGists}
      currentGistId={gist.id}
      selectedSubjectId={selectedSubject?.id ?? null}
      getGistHref={(targetGist) => getWorkspaceGistUrl(targetGist, readerMode)}
      onSelectGist={(targetGist) => openGistReader(targetGist, readerMode)}
      onRequestHide={() => setWorkspaceNavigatorHidden(true)}
    />
  );

  const renderGistReaderPage = (gist: GistItem) => (
    <>
      {renderSubjectHero()}
      {renderModeSwitch()}
      <div
        className={`${styles.workspaceReaderLayout} ${
          workspaceNavigatorHidden ? styles.workspaceReaderLayoutFull : ""
        }`}
        style={
          {
            "--workspace-navigator-width": `${workspaceNavigatorWidth}px`,
          } as React.CSSProperties
        }
      >
        {!workspaceNavigatorHidden && (
          <>
            <aside
              id="workspace-branch-navigation"
              ref={workspaceNavigatorRef}
              className={styles.workspaceReaderNavigator}
              tabIndex={-1}
              aria-label="Workspace branch navigation panel"
            >
              {renderWorkspaceNavigator(gist)}
            </aside>
            <button
              type="button"
              className={styles.workspaceReaderResizeHandle}
              onPointerDown={startWorkspaceNavigatorResize}
              aria-label="Resize branch navigation panel"
              title="Drag to resize navigation"
            >
              <span aria-hidden="true" />
            </button>
          </>
        )}
        <section className={styles.workspaceReaderContent}>
          {workspaceNavigatorHidden && (
            <div className={styles.workspaceNavigatorPillRow}>
              <button
                type="button"
                className={styles.workspaceNavigatorPill}
                onClick={revealWorkspaceNavigator}
              >
                Navigation
              </button>
            </div>
          )}
          {readerMode === "read" && renderReadMode(gist)}
          {readerMode === "tools" && renderToolsMode(gist)}
          {readerMode === "advanced" && renderAdvancedMode(gist)}
        </section>
      </div>
    </>
  );

  const clearPendingSubjectClick = () => {
    if (clickTimeoutRef.current !== null) {
      window.clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }
  };

  const startGistExplorerResize = (
    dividerIndex: 0 | 1,
    event: React.PointerEvent<HTMLButtonElement>,
  ) => {
    const grid = gistExplorerGridRef.current;
    if (!grid) return;

    event.preventDefault();
    event.stopPropagation();

    const rect = grid.getBoundingClientRect();
    const splitterWidth = 24;
    const availableWidth = Math.max(360, rect.width - splitterWidth);
    const initialWeights = gistExplorerColumnWeightsRef.current;
    const weightTotal = initialWeights.reduce((total, value) => total + value, 0);
    const initialWidths = initialWeights.map(
      (weight) => (weight / weightTotal) * availableWidth,
    );
    const startX = event.clientX;
    const minWidths = [72, 96, 140];

    document.body.classList.add(styles.gistExplorerResizingBody);

    const handlePointerMove = (moveEvent: PointerEvent) => {
      const delta = moveEvent.clientX - startX;
      const nextWidths = [...initialWidths];

      if (dividerIndex === 0) {
        const pairTotal = initialWidths[0] + initialWidths[1];
        const nextLeft = Math.min(
          pairTotal - minWidths[1],
          Math.max(minWidths[0], initialWidths[0] + delta),
        );
        nextWidths[0] = nextLeft;
        nextWidths[1] = pairTotal - nextLeft;
      } else {
        const pairTotal = initialWidths[1] + initialWidths[2];
        const nextMiddle = Math.min(
          pairTotal - minWidths[2],
          Math.max(minWidths[1], initialWidths[1] + delta),
        );
        nextWidths[1] = nextMiddle;
        nextWidths[2] = pairTotal - nextMiddle;
      }

      setGistExplorerColumnWeights(nextWidths);
    };

    const handlePointerUp = () => {
      document.body.classList.remove(styles.gistExplorerResizingBody);
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);
  };

  const resetGistExplorerColumns = () => {
    setGistExplorerColumnWeights([26, 46, 28]);
  };

  const renderGistExplorerPage = () => (
    <GistExplorerPage
      selectedSubject={selectedSubject}
      selectedGist={gistExplorerSelectedGist}
      branchGists={activeGists}
      gistTree={gistTree}
      visibleSubjectRows={visibleSubjectRows}
      subjectChildrenById={buckets}
      effectiveSubjectExpandedIds={effectiveSubjectExpandedIds}
      subjectSearchMatchingIds={subjectSearchMatchingIds}
      gistExplorerColumnWeights={gistExplorerColumnWeights}
      gistExplorerGridRef={gistExplorerGridRef}
      rowButtonRefs={rowButtonRefs}
      isRegisteredUser={isRegisteredUser}
      renderSubjectSearch={renderSubjectSearch}
      renderSubjectSearchEmpty={renderSubjectSearchEmpty}
      renderHighlightedSubjectTitle={renderHighlightedSubjectTitle}
      onBackToSubjects={() => navigate("/subject-single")}
      onChooseSubject={chooseSubject}
      onCreateGist={openCreateGistModal}
      onCreateLinkedDraft={openLinkedDraftModal}
      onToggleSubjectExpanded={toggleExpanded}
      onFocusSubject={setFocusedSubjectId}
      onSubjectClick={handleSubjectClick}
      onSubjectDoubleClick={handleSubjectDoubleClick}
      onClearPendingSubjectClick={clearPendingSubjectClick}
      onShowSubjectActionMenu={showSubjectActionMenu}
      onSelectGist={setGistExplorerSelectedId}
      onOpenGistReader={openGistReader}
      onStartResize={startGistExplorerResize}
      onResetColumns={resetGistExplorerColumns}
      onSubjectTreeKeyDown={handleSubjectTreeKeyDown}
    />
  );

  const renderSubjectRow = (
    subject: SubjectItem,
    depth: number,
  ): React.ReactNode => {
    const children = buckets[String(subject.id)] || [];
    const hasChildren = children.length > 0;
    const isExpanded = effectiveSubjectExpandedIds.has(subject.id);
    const stat = subjectStats[subject.id];
    const isFocused = focusedSubjectId === subject.id;
    const isSearchMatch = subjectSearchMatchingIds.has(subject.id);

    return (
      <div
        key={subject.id}
        className={`${styles.treeRow} ${isSearchMatch ? styles.treeRowSearchMatch : ""}`}
        style={{ "--depth": depth } as React.CSSProperties}
        role="treeitem"
        aria-level={depth + 1}
        aria-expanded={hasChildren ? isExpanded : undefined}
        aria-selected={isFocused}
      >
        <button
          type="button"
          className={styles.expandButton}
          aria-label={
            hasChildren
              ? isExpanded
                ? `Collapse ${subject.title}`
                : `Expand ${subject.title}`
              : `${subject.title} has no child subjects`
          }
          onClick={(event) => {
            event.stopPropagation();
            setFocusedSubjectId(subject.id);
            if (hasChildren) toggleExpanded(subject.id);
          }}
          disabled={!hasChildren}
          tabIndex={-1}
        >
          {hasChildren ? (isExpanded ? "⌄" : "›") : ""}
        </button>

        {depth > 0 && (
          <span className={styles.treeConnector} aria-hidden="true" />
        )}

        <button
          type="button"
          ref={(node) => {
            rowButtonRefs.current[subject.id] = node;
          }}
          className={styles.subjectOpenButton}
          aria-label={`${subject.title}. Click once to open a collapsed branch, press Enter to view gists, or double click for more actions.`}
          onFocus={() => setFocusedSubjectId(subject.id)}
          onClick={(event) => handleSubjectClick(event, subject, false)}
          onDoubleClick={(event) => handleSubjectDoubleClick(event, subject)}
        >
          <span className={styles.folderIcon} aria-hidden="true" />
          <strong>{renderHighlightedSubjectTitle(subject.title)}</strong>
        </button>

        <div className={styles.rowStats} aria-hidden="true">
          <span>{compactNumber(stat?.branchGistCount || 0)} Gists</span>
          <span>{compactNumber(stat?.commentCount || 0)} Debates</span>
          <span className={styles.activeState}>
            <i /> Active
          </span>
        </div>

        <button
          type="button"
          className={styles.rowActionButton}
          aria-label={`Open actions for ${subject.title}`}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (clickTimeoutRef.current !== null) {
              window.clearTimeout(clickTimeoutRef.current);
              clickTimeoutRef.current = null;
            }
            showSubjectActionMenu(subject, event.clientX, event.clientY);
          }}
        >
          ⋯
        </button>
      </div>
    );
  };

  const commentWorkspaceGist = commentWorkspace
    ? gists.find((gist) => gist.id === commentWorkspace.gistId) ||
      (detailGist?.id === commentWorkspace.gistId ? detailGist : null)
    : null;
  const commentWorkspaceEdit = commentWorkspaceGist ? getLatestEdit(commentWorkspaceGist) : null;
  const routeSubjectMissing = Boolean(
    routeSubjectPublicId && !subjects.some((subject) => subject.publicId === routeSubjectPublicId),
  );
  const routeGistMissing = Boolean(
    routeGistPublicId && !gists.some((gist) => gist.publicId === routeGistPublicId),
  );
  const routeTargetMissing = !loading && !error && (routeSubjectMissing || routeGistMissing);

  return (
    <main className={styles.page}>
      <SubjectChooserDrawer
        drawerOpen={drawerOpen}
        selectedSubject={selectedSubject}
        visibleSubjectRows={visibleSubjectRows}
        renderSubjectSearch={renderSubjectSearch}
        renderSubjectSearchEmpty={renderSubjectSearchEmpty}
        renderSubjectRow={renderSubjectRow}
        onToggleDrawer={() => setDrawerOpen((current) => !current)}
        onCloseDrawer={() => setDrawerOpen(false)}
        onSubjectTreeKeyDown={handleSubjectTreeKeyDown}
      />

      <SubjectActionMenu
        actionMenu={actionMenu}
        menuRef={actionMenuRef}
        isRegisteredUser={isRegisteredUser}
        isSubjectModerator={isSubjectModerator}
        onClose={() => closeActionMenu()}
        onKeyDown={handleActionMenuKeyDown}
        onChooseSubject={chooseSubject}
        onViewSubjectGists={viewSubjectGists}
        onOpenSubjectModal={openSubjectModal}
      />

      <div className={styles.shell}>
        <SubjectWorkspaceBreadcrumb
          selectedPath={selectedPath}
          onResetSubjects={() => navigate("/subject-single")}
          onChooseSubject={chooseSubject}
        />

        {loading && (
          <div className={styles.statePanel}>
            Loading real subjects and gists...
          </div>
        )}
        {error && (
          <div className={styles.errorPanel}>
            Unable to load live data: {error.message}
          </div>
        )}
        {routeTargetMissing && (
          <div className={styles.errorPanel}>
            This workspace link does not match an existing publicId. The object may have been deleted or the URL may be outdated.
          </div>
        )}

        {!loading && !error && !routeTargetMissing && !selectedSubject && (
          <SubjectPickerPanel
            pickerMode={pickerMode}
            isSubjectSearchActive={isSubjectSearchActive}
            visibleSubjectRows={visibleSubjectRows}
            footerSubject={footerSubject}
            renderSubjectSearch={renderSubjectSearch}
            renderSubjectSearchEmpty={renderSubjectSearchEmpty}
            renderSubjectRow={renderSubjectRow}
            onSetPickerMode={setPickerMode}
            onExpandAll={expandAll}
      onCollapseAll={collapseAll}
            onChooseSubject={chooseSubject}
            onSubjectTreeKeyDown={handleSubjectTreeKeyDown}
          />
        )}

        {!loading && !error && !routeTargetMissing && selectedSubject && selectedStats && (
          <>
            {detailGist ? (
              renderGistReaderPage(detailGist)
            ) : isGistExplorerView ? (
              renderGistExplorerPage()
            ) : (
              <SubjectOverviewPage
                selectedSubject={selectedSubject}
                featuredSlots={featuredSlots}
                openedGist={openedGist}
                subjectHero={renderSubjectHero()}
                onOpenGistReader={openGistReader}
                onCreateLinkedDraft={openLinkedDraftModal}
                getSubjectUrl={getSubjectUrl}
                isRegisteredUser={isRegisteredUser}
                directGistCount={directGists.length}
                branchGistCount={branchGists.length}
                childSubjectCount={selectedStats.childCount}
              />
            )}
          </>
        )}
      </div>

      {commentWorkspace && commentWorkspaceGist && (
        <GistCommentWorkspacePanel
          gistId={commentWorkspaceGist.id}
          editId={commentWorkspaceEdit?.id ?? null}
          title={commentWorkspaceGist.title}
          subtitle="Canonical Comment Workspace"
          user={user}
          surface={commentWorkspace.surface}
          selectedCommentId={commentWorkspace.selectedCommentId}
          selectedAnchorId={commentWorkspace.selectedAnchorId}
          onSelectedCommentChange={(selectedCommentId) =>
            setCommentWorkspace((current) =>
              current && current.selectedCommentId !== selectedCommentId
                ? { ...current, selectedCommentId }
                : current,
            )
          }
          onSelectedAnchorChange={(selectedAnchorId) =>
            setCommentWorkspace((current) =>
              current && current.selectedAnchorId !== selectedAnchorId
                ? { ...current, selectedAnchorId }
                : current,
            )
          }
          onRequestExpand={() => setCommentWorkspaceSurface("expanded")}
          onRequestDock={() => setCommentWorkspaceSurface("docked")}
          onRequestClose={closeCommentWorkspace}
        />
      )}

      <CreateGistModal
        visible={Boolean(createGistTarget)}
        onClose={closeCreateGistModal}
        subjectId={createGistTarget?.subjectId}
        parentGistId={createGistTarget?.parentGistId}
        defaultSaveAsDraft={createGistTarget?.defaultSaveAsDraft}
        intent={createGistTarget?.intent}
      />

      <NodeActionModal
        visible={modalVisible}
        position={modalPosition}
        node={modalSubject}
        onClose={closeSubjectModal}
        setModalAction={setModalAction}
        modalAction={modalAction}
      />
    </main>
  );
};

export default SubjectSinglePage;
